<script lang="ts">
  import { browser } from "$app/environment";
  import LinkComp from "$components/link.svelte";
  import { lockPage, unlockPage } from "$lib/store/helper-context";
  import { utils } from "$lib/utils";
  import { onDestroy, onMount } from "svelte";
  import type { ISearchEventResults } from "../../routes/(public-api)/z/home/[...path]/+server";
  import { isLoading } from "$lib/store/loading-context";

  export let locale = "en";

  let searchTerm = "";
  let lastSearchTerm = "";
  let showOverlay = false;
  let events: ISearchEventResults[] = [];
  let controller: AbortController | undefined;

  function searchFixture(ev: KeyboardEvent) {
    ev.stopImmediatePropagation();
    const { value } = ev.target as HTMLInputElement;
    searchTerm = (value || "").trim();

    if (!searchTerm || searchTerm.length <= 0) {
      events = [];
      lastSearchTerm = searchTerm;
      closeOverlay();
      return;
    }

    // For when pressing shift or enter etc.
    if (searchTerm === lastSearchTerm) {
      return;
    }
    const perPage = "10";
    const params = new URLSearchParams({ title: searchTerm, perPage, locale });
    lastSearchTerm = searchTerm;
    controller && controller.abort("fetch abort by user");
    controller = new AbortController();
    fetch(`/z/home/events?${params.toString()}`, { signal: controller.signal })
      .then(resp => resp.json())
      .then(data => {
        events = data;
        openOverlay();
      })
      .catch(err => {
        if (err === "fetch abort by user") {
          console.info(err);
        } else {
          console.error(err);
        }
        events = [];
        closeOverlay();
      })
      .finally(() => {
        controller = undefined;
      });
  }

  function openOverlay(ev?: Event) {
    ev?.stopImmediatePropagation();
    if (events?.length) {
      lockPage();
      showOverlay = true;
    }
  }

  function closeOverlay(ev?: Event) {
    ev?.stopImmediatePropagation();
    showOverlay = false;
    unlockPage();
  }

  function handleSubmit(ev: Event) {
    ev.preventDefault();
    ev.stopImmediatePropagation();
    if (searchTerm) {
      formEl && formEl.submit();
    }
  }

  function handleGotoPage(ev: Event) {
    ev.stopImmediatePropagation();
    isLoading.set(true);
    unlockPage();
  }

  let viewAllEl: HTMLElement;
  let formEl: HTMLFormElement;

  onMount(() => {
    isLoading.set(false);
    if (!browser) {
      return;
    }
    viewAllEl.addEventListener("click", handleSubmit);
  });

  onDestroy(() => {
    viewAllEl && viewAllEl.removeEventListener("click", handleSubmit);
    unlockPage();
    isLoading.set(false);
  });
</script>

<div
  class="overlay"
  class:show={events?.length && showOverlay}
  on:click={closeOverlay}
  on:keydown={closeOverlay}
  role="button"
  tabindex="0"
/>
<form action="/search" method="GET" bind:this={formEl} on:submit={handleSubmit}>
  <div class="search" class:priority={events?.length && showOverlay}>
    <input placeholder={"Search"} on:keyup={searchFixture} type="text" name="q" on:click={openOverlay} />
    <button class="open-search">
      <i class="search-icon" />
      <span class="sf-500">Search</span>
    </button>

    <div class="results" class:show={events?.length && showOverlay}>
      <div class="sf-700 title">Results - Upcoming games</div>
      <ol>
        {#each events as event}
          {@const { title, slug, datetime, subcategory, tbc } = event || {}}
          <li class="pf-400">
            <LinkComp href={`${subcategory?.slug}${slug}`} on:click={handleGotoPage}>
              <dl>
                <dt class="sf-500">{title}</dt>
                <dd class="sf-300 league">
                  {subcategory?.title},
                  {#if tbc?.date}
                    TBC
                  {:else}
                    {utils.getDateAndTime(datetime, locale, 3, tbc)}
                  {/if}
                </dd>
              </dl>
            </LinkComp>
          </li>
        {/each}
      </ol>

      <div class="sf-600 view-all" bind:this={viewAllEl}>View all results</div>
    </div>
  </div>
</form>

<style lang="scss">
  form {
    margin: 0;
  }

  .overlay {
    z-index: var(--z2);
    background: rgba(0, 0, 0, 0.2);
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    display: none;

    &.show {
      display: block;
    }
  }

  .search {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    height: 3.125em;
    background: #fff;
    border-radius: 0.3125em;
    font-size: 16px;
    @media (min-width: 1336px) {
      width: 33.625em;
    }

    &.priority {
      z-index: var(--z2);
    }
  }

  ol {
    list-style: none;
    margin-bottom: 0;
    max-height: 26em;
    overflow-y: scroll;
    /* Remove scrollbar width */
    /* &::-webkit-scrollbar {
      width: 0;
    }

    &::-webkit-scrollbar-track {
      display: none;
    }

    &::-webkit-scrollbar-thumb {
      display: none;
    } */

    dt,
    dd {
      line-height: 1.4em;
      color: #000;
    }
    .league {
      color: #6e6e6e;
    }

    li {
      text-align: left;
      padding: 0.5em;
      &:hover {
        background: #ededed;
        border-radius: 0.5em;
        transform: translate(-1px, 1px);
      }

      &:last-child {
        border: 0;
      }
    }
  }

  .results {
    display: none;
    position: absolute;
    top: 3.5em;
    left: 0;
    right: 0;
    background: #fff;
    color: #000;
    border-radius: 0.625em;
    padding: 1em 1.5em 1em 1.69em;

    &.show {
      display: grid;
    }

    .title {
      color: rgba(0, 0, 0, 0.7);
      font-size: 1.125em;
      font-weight: 700;
      line-height: 1.11em;
      border-bottom: 1px solid #eaeaea;
      padding: 0.33em 0 0.55em;
    }

    .view-all {
      background: #fff;
      border-top: 1px solid #eaeaea;
      padding-top: 11px;
      height: 36px;
      color: rgba(0, 0, 0, 0.7);
      line-height: 20px;
      &:hover {
        cursor: pointer;
        color: var(--primary-hover);
        text-decoration: underline;
      }
    }
  }

  .search-icon {
    background-image: url(./search-icon.svg);
    background-size: contain;
    width: 1.25em;
    height: 1.25em;
    background-repeat: no-repeat;
  }

  input {
    height: 50px;
    flex: 1;
    border: none;
    margin: 0;
    position: relative;
    height: 50px;
    &::selection {
      background-color: rgba(#283a58, 0.25);
    }
    &::placeholder {
      max-width: 90%;
      overflow: hidden;
      font-weight: 300;
    }
  }

  .open-search {
    margin-right: 0.16em;
    width: 3.375em;
    height: 2.75em;
    border-radius: 0.3125em;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #3649ce;
    transition: width 0.3s;
    font-size: 16px;
    @media (min-width: 1336px) {
      width: 8.75em;
    }
    // reinstate for hover , remove include desktop for open-search and p tag
    // &:hover {
    //   width: 8.75em;
    //   p {
    //     width: 4.375em;
    //     margin-left: 0.315em;
    //   }
    // }

    span {
      display: none;
      font-size: 1.125rem;
      @media (min-width: 1336px) {
        display: inline;
        width: 3.56em;
        margin-left: 0.25em;
      }
    }
  }
</style>
